import { useEffect, useState } from 'react'
import { api } from '../api/client'

let cachedOrder: Map<string, number> | null = null
let inFlight: Promise<Map<string, number>> | null = null
const subscribers = new Set<(m: Map<string, number>) => void>()

function sortCases(cases: { id: string; selection_kind?: string }[]) {
  // Mirror the order Cases.tsx renders in: smart-curated cases first, then the
  // random fillers. Stable within each group.
  return [...cases].sort((a, b) => {
    const aSmart = a.selection_kind === 'smart' ? 0 : 1
    const bSmart = b.selection_kind === 'smart' ? 0 : 1
    return aSmart - bSmart
  })
}

function loadOrder(): Promise<Map<string, number>> {
  if (cachedOrder) return Promise.resolve(cachedOrder)
  if (inFlight) return inFlight
  inFlight = api.listCases()
    .then((r) => {
      const map = new Map<string, number>()
      sortCases(r.cases).forEach((c, i) => map.set(c.id, i + 1))
      cachedOrder = map
      subscribers.forEach((cb) => cb(map))
      return map
    })
    .catch((err) => {
      inFlight = null
      throw err
    })
  return inFlight
}

export function caseDisplayNumber(id: string): number | null {
  return cachedOrder?.get(id) ?? null
}

export function caseDisplayName(id: string, fallback?: string): string {
  const n = caseDisplayNumber(id)
  if (n != null) return `Vaka #${n}`
  return fallback ?? id
}

export function useCaseNumber(id: string | null | undefined): number | null {
  const [n, setN] = useState<number | null>(() => (id ? caseDisplayNumber(id) : null))
  useEffect(() => {
    if (!id) {
      setN(null)
      return
    }
    setN(caseDisplayNumber(id))
    if (cachedOrder) return
    let live = true
    const sub = (m: Map<string, number>) => {
      if (live) setN(m.get(id) ?? null)
    }
    subscribers.add(sub)
    loadOrder().catch(() => {})
    return () => {
      live = false
      subscribers.delete(sub)
    }
  }, [id])
  return n
}

export function primeCaseIndex(cases: { id: string; selection_kind?: string }[]): void {
  const map = new Map<string, number>()
  sortCases(cases).forEach((c, i) => map.set(c.id, i + 1))
  cachedOrder = map
  subscribers.forEach((cb) => cb(map))
}
